import angular from 'angular';
import selectedElemTemplate from './templates/dropdown-selected.tpl.html';
import menuElemTemplate from './templates/dropdown-menu.tpl.html';

const PLACEHOLDER_KEY = 'components.dropdown.placeholder.noSelection';

export default class DropdownController {

  static get $inject() {
    return ['$scope', '$parse', '$translate', 'dropdownTemplateService', 'appendToBodyService',
      '$compile', '$log', '$q', '$element', '$attrs'];
  }

  constructor($scope, $parse, $translate, dropdownTemplateService, appendToBodyService, $compile,
              $log, $q, $element, $attrs) {
    this.$scope = $scope;
    this.$compile = $compile;
    this.$log = $log;
    this.$q = $q;
    this.$translate = $translate;
    this.$parse = $parse;
    this.dropdownTemplateService = dropdownTemplateService;
    this.appendToBodyService = appendToBodyService;
    this.$element = $element;
    this.$attrs = $attrs;
  }

  $onInit() {
    this.name = 'dropdown';
    this.hasTextProperty = angular.isDefined(this.textProperty);
    this.textPropertyFn = this.$parse(this.textProperty);
    this.disabledPropertyFn = this.$parse(this.disabledProperty);
    this.textPropertySetter = this.textPropertyFn.assign;
    this.isOpen = false;
    this.itemSet = {};
    this.resolvedItems = [];
    this.templateData = {
      selected: {
        template: selectedElemTemplate,
        customSelector: 'span.selected-option'
      },
      menu: {
        template: menuElemTemplate,
        customSelector: 'a.dropdown-item-link'
      }
    };

    this.promiseLoading = false;
    this.promiseError = false;

    this.noItemsMessage = this.noItemsMessage ||
      'components.dropdown.noItemsMessage';

    this.itemFailureMessage = this.itemFailureMessage ||
      'components.dropdown.itemFailureMessage';
  }

  initialize() {
    this.ngModel.$render = () => {
      this.selectedItem = this.ngModel.$viewValue;
    };

    this.$scope.$watchCollection(`${this.name}.items`, items => {
      this.createItemMap(items);
    });

    if (angular.isDefined(this.$attrs.keyProperty)) {
      this.keyProperty = this.$attrs.keyProperty;
      this.keyPropertyFn = this.$parse(this.keyProperty || 'id');

      this.createItemMap(this.items);
    }

    this.isClearable = angular.isDefined(this.$attrs.clearable);
    this.appendToBody = angular.isDefined(this.$attrs.appendedToBody);

    this.selected =
      new this.dropdownTemplateService.DropdownSelectedRenderer(this);
    this.menu =
      new this.dropdownTemplateService.DropdownMenuRenderer(this);

    this.setPlaceholders();

    this.selected.render();
    this.menu.render();
  }

  hasSelectedItem() {
    // only undefined is considered as no selectedItem
    return angular.isDefined(this.selectedItem);
  }

  getSelectedItemText() {
    if (this.keyProperty && this.hasSelectedItem()) {
      return this.textPropertyFn(this.itemSet[this.selectedItem]);
    } else if (this.textProperty) {
      return this.textPropertyFn(this.selectedItem);
    } else if (angular.isString(this.selectedItem)) {
      return this.selectedItem;
    }
  }

  clickSelected(event) {
    if (this.isDisabled || this.isReadonly) {
      // disabled and readonly shouldnt prop
      event.stopPropagation();
    }
  }

  createItemMap(items) {
    this.promiseLoading = true;
    this.promiseError = false;
    this.resolvedItems = [];

    this.$q.when(items)
      .then((promiseItems) => {
        this.untranslatedResolvedItems = promiseItems && promiseItems.hasOwnProperty('data') &&
          angular.isDefined(promiseItems.data) ? promiseItems.data : promiseItems;

        this.itemSet = {};

        if (this.keyPropertyFn) {
          angular.forEach(this.untranslatedResolvedItems, (item) => {
            const keyId = this.keyPropertyFn(item);

            if (!(this.itemSet.hasOwnProperty(keyId) && this.itemSet[keyId])) {
              this.itemSet[keyId] = item;
            } else {
              throw new Error('Keys must be unique when using the key-property attribute');
            }
          });
        }

        this.translateTextProperty().then(() => {
          this.resolvedItems = this.untranslatedResolvedItems;

          if (this.resolvedItems && this.resolvedItems.length > 0 &&
            angular.isString(this.resolvedItems[0]) && angular.isDefined(this.disabledProperty)) {

            const errorMessage =
              'Provided items must be Objects when using the disabled-property attribute';
            throw new Error(errorMessage);
          }

        });

      })
      .catch(() => {
        this.resolvedItems = [];
        this.promiseError = true;
      })
      .finally(() => {
        this.promiseLoading = false;
      });
  }

  setPlaceholders() {
    this.$translate(this.placeholder || PLACEHOLDER_KEY)
      .then(value => {
        this.placeholder = value;
        this.selected.placeholder = this.placeholder;
      })
      .catch(value => {
        this.placeholder = value;
        this.selected.placeholder = this.placeholder;
      });
  }

  translateTextProperty() {
    const translationPromise = [];

    if (angular.isDefined(this.textProperty)) {
      if (angular.isDefined(this.keyProperty)) {
        Object.keys(this.itemSet).forEach(key => {
          const translatePromise = this.$q.defer();
          translationPromise.push(translatePromise.promise);

          this.$translate(this.textPropertyFn(this.itemSet[key]), this.itemSet[key])
            .then(value => {
              this.textPropertySetter(this.itemSet[key], value);
              translatePromise.resolve(value);
            })
            .catch(value => {
              this.textPropertySetter(this.itemSet[key], value);
              translatePromise.resolve(value);
            });
        });
      } else {
        angular.forEach(this.untranslatedResolvedItems, (item) => {
          const translatePromise = this.$q.defer();
          translationPromise.push(translatePromise.promise);

          this.$translate(item[this.textProperty], item)
            .then(value => {
              item[this.textProperty] = value;
              translatePromise.resolve(value);
            })
            .catch(value => {
              item[this.textProperty] = value;
              translatePromise.resolve(value);
            });
        });
      }
    } else {
      if (!this.untranslatedResolvedItems) {
        return this.$q.resolve();
      }

      const len = this.untranslatedResolvedItems.length;

      if ( len && typeof this.untranslatedResolvedItems[0] === 'object') {
        return this.$q.resolve();
      }

      for (let i = 0; i < len; i++) {
        const translatePromise = this.$q.defer();
        translationPromise.push(translatePromise.promise);

        this.$translate(this.untranslatedResolvedItems[i])
          .then(value => {
            this.untranslatedResolvedItems[i] = value;
            translatePromise.resolve(value);
          })
          .catch(value => {
            this.untranslatedResolvedItems[i] = value;
            translatePromise.resolve(value);
          });
      }
    }

    return this.$q.all(translationPromise);
  }

  setSelectedItem(item, e) {
    // stop propagation on anchor behavior
    e.stopPropagation();

    if (this.isItemDisabled(item)) {
      e.preventDefault();
      return;
    }

    this.scrollBarClicked = false;
    item = this.keyProperty ? this.keyPropertyFn(item) : item;

    this.selectedItem = item;
    this.selected.selectedItem = this.selectedItem;

    this.isOpen = false;

    this.ngModel.$setViewValue(this.selectedItem);
    this.ngModel.$setTouched();
  }

  clearSelectedItem($event) {
    // we want to the dropdown to remain open when clearing the value
    // this may prevent other open dropdowns from closing however
    $event.stopPropagation();
    $event.preventDefault();
    this.ngModel.$setViewValue();
    this.ngModel.$setTouched();
    this.selectedItem = undefined;
  }

  renderMenu() {
    return angular.isArray(this.resolvedItems);
  }

  getItems() {
    return this.renderMenu() ? this.resolvedItems : [];
  }

  isItemDisabled(item) {
    if (angular.isFunction(this.disabledPropertyFn(item))) {
      return this.disabledPropertyFn(item)() === true;
    }
    return this.disabledPropertyFn(item) === true;
  }

  setTouchedState() {
    this.ngModel.$setTouched();
  }
}
